import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Navigate, Link, useNavigate } from 'react-router-dom';
import { BasecampLayout, AreaLayout } from '../../components/Layout';
import { PageHeader, Card, Spinner, Alert, Table, Btn, Input, Select, Badge, Modal, SectionCard, SectionLabel, Note } from '../../components/UI';
import { api } from '../../utils/api';
import PhaseOverview from '../phase/PhaseOverview';
import PhaseNotes from '../phase/PhaseNotes';
import DesignEditor, { DesignStart } from './DesignEditor';

// Master — translacioni sloj (Faza 3B / WP-3). Dva moda po spec §11.2:
// informative (Methodology, Signature Profiles learn) i implementation
// (uvek ankeriran na projekat): engagement → baseline profil → adaptacija
// (svaka faseta pregledana, per-dimension sign-off, mandatory 🔒 zaštićene)
// → matching → Programme Builder → proposal → closing.

// One Workplace, One Shelf (W4): Master is the SHELF only — method, profiles, facet library,
// reference case. Needs/Design/Proposal moved into the customer workspace (My Customers → a
// customer). No project-bound screens live here anymore.
const SUBNAV = [
  { href: '/master/overview', label: 'Ice Pilot as a Master', isHeaderLink: true },
  { header: 'Hansen Beck Way', collapsible: true },
  { href: '/master/method', label: 'Methodology', indent: true },
  { href: '/master/profiles', label: 'Hansen Beck Signature Profiles', indent: true },
  { href: '/master/ypl', label: 'Reference Case', indent: true },
];

const ENGAGEMENT_KEY = 'basecamp_engagement_project';
const ROLETRACK_KEY = 'basecamp_roletrack';

// One Workplace, One Shelf (W3): the customer workspace embeds Needs/Design/Proposal.
// Those screens read the active project/track from localStorage (useEngagement/useRoleTrack).
// The workspace sets the context here, then remounts the embedded screen via a React `key`
// so it re-reads the fresh values — routing + embedding only, no rewrite of the screens.
// Embedded in the customer workspace, a gate or proposal change moves the stage — tell the
// workspace so its journey menu and HB Way of Selling panel reload (no-op on its own).
const notifyWorkspace = () => window.dispatchEvent(new CustomEvent('hb-engagement-changed'));

// "Continue to …" after a gate: inside the customer workspace it moves to that step there
// (the workspace cancels the event); on the standalone Master pages it is a plain link.
function ContinueLink({ to, step, children }) {
  const onClick = (e) => {
    const ev = new CustomEvent('hb-workspace-go', { detail: { step }, cancelable: true });
    if (!window.dispatchEvent(ev)) e.preventDefault();
  };
  return <Link to={to} onClick={onClick} style={primaryLinkStyle}>{children}</Link>;
}

export function setWorkspaceContext(projectId, trackId) {
  if (projectId) localStorage.setItem(ENGAGEMENT_KEY, String(projectId));
  else localStorage.removeItem(ENGAGEMENT_KEY);
  if (trackId) localStorage.setItem(ROLETRACK_KEY, String(trackId));
  else localStorage.removeItem(ROLETRACK_KEY);
}

function Loading() {
  return <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}><Spinner /></div>;
}

// Aktivni engagement (izabrani projekat) — deli se između podstrana
function useEngagement() {
  const [projectId, setProjectIdState] = useState(() => {
    const v = localStorage.getItem(ENGAGEMENT_KEY);
    return v ? parseInt(v, 10) : null;
  });
  const setProjectId = (id) => {
    if (id) localStorage.setItem(ENGAGEMENT_KEY, String(id));
    else localStorage.removeItem(ENGAGEMENT_KEY);
    setProjectIdState(id);
  };
  return [projectId, setProjectId];
}

// Aktivni role track — deli se između Needs i Design
function useRoleTrack() {
  const [trackId, setState] = useState(() => {
    const v = localStorage.getItem(ROLETRACK_KEY);
    return v ? parseInt(v, 10) : null;
  });
  const setTrackId = (id) => {
    if (id) localStorage.setItem(ROLETRACK_KEY, String(id));
    else localStorage.removeItem(ROLETRACK_KEY);
    setState(id);
  };
  return [trackId, setTrackId];
}

function useEngagementData(projectId) {
  const [state, setState] = useState({ loading: !!projectId, error: null, data: null });
  const load = useCallback(() => {
    if (!projectId) { setState({ loading: false, error: null, data: null }); return; }
    setState(s => ({ ...s, loading: true }));
    api.master.getEngagement(projectId)
      .then(d => setState({ loading: false, error: null, data: d }))
      .catch(e => setState({ loading: false, error: e.message, data: null }));
  }, [projectId]);
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

function EngagementBanner({ data, onClose }) {
  if (!data) return null;
  return (
    <Alert type="info">
      <b>Active engagement:</b> {data.project.name} · {data.project.customer}
      {data.profile ? ` · profile: ${data.profile.baseProfile} (${data.profile.status})` : ''}
      <button onClick={onClose} style={{ marginLeft: 12, textDecoration: 'underline', fontSize: '0.82rem', color: 'inherit' }}>
        Close engagement
      </button>
    </Alert>
  );
}

// ── Methodology ───────────────────────────────────────────────────────────
// "The engagement — a guided path" mirrors the stages ("points") of the active
// HB Way of Selling process, edited in Admin → Sales Process Designer. Rendering
// them here (rather than a hardcoded list) keeps Master, the Designer and CRM →
// Pipeline by stage on one source of truth.
const ANCHOR_LABEL = { E: 'Explore', M: 'Master', D: 'Deliver' };

function stageWhere(s) {
  if (!s.primaryActionLabel) return '—';
  const route = s.primaryActionRoute || '';
  if (/^https?:\/\//i.test(route)) {
    return <a href={route} target="_blank" rel="noreferrer" style={{ textDecoration: 'underline' }}>{s.primaryActionLabel}</a>;
  }
  if (route.startsWith('/')) {
    return <Link to={route} style={{ textDecoration: 'underline' }}>{s.primaryActionLabel}</Link>;
  }
  return s.primaryActionLabel;
}

function Method() {
  const [process, setProcess] = useState(undefined); // undefined = loading, null = none
  useEffect(() => {
    api.getProcess().then(d => setProcess(d.process || null)).catch(() => setProcess(null));
  }, []);
  const stages = process && process.stages ? process.stages : [];

  return (
    <>
      <Alert type="info">
        The middleware phase: position the customer's needs against a signature profile, adapt facets,
        and combine Units into a meaningful programme. This section is the working reference —
        personal development lives in Grow.
      </Alert>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, margin: '16px 0 24px' }}>
        {['HB Compass philosophy', 'Working with signature profiles', 'Adaptation guideline (Map of Adaptations)',
          'Focus Briefs — narrowing to a capability', 'Master facet vocabulary'].map(t => (
          <Card key={t} style={{ padding: '12px 16px', fontSize: '0.88rem', color: 'var(--ink-soft)' }}>▪ {t}</Card>
        ))}
      </div>
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 10 }}>
        The engagement — a guided path
      </h3>
      {process === undefined ? <Loading /> : stages.length === 0 ? (
        <Alert type="warn">No active sales process is configured yet.</Alert>
      ) : (
        <>
          <p style={{ fontSize: '0.8rem', color: 'var(--ink-faint)', marginBottom: 8 }}>
            {process.name} v{process.version} — the stages of HB Way of Selling.
          </p>
          <Card style={{ padding: '10px 18px' }}>
            <Table
              headers={['Stage', 'What happens', 'Where']}
              rows={stages.map(s => [
                <span>{s.seq} · {s.name}{s.anchor ? <span style={{ color: 'var(--ink-faint)', fontSize: '0.78rem' }}> · {ANCHOR_LABEL[s.anchor] || s.anchor}</span> : null}</span>,
                s.entryCriterion || '—',
                stageWhere(s),
              ])}
            />
          </Card>
        </>
      )}
    </>
  );
}

// ── Signature Profiles (learn) ────────────────────────────────────────────
function ProfilesLearn() {
  const [state, setState] = useState({ loading: true, error: null, profiles: [] });
  const [open, setOpen] = useState(null);
  useEffect(() => {
    api.master.getSignatureProfiles()
      .then(d => setState({ loading: false, error: null, profiles: d.profiles }))
      .catch(e => setState({ loading: false, error: e.message, profiles: [] }));
  }, []);

  if (state.loading) return <Loading />;
  if (state.error) return <Alert type="error">{state.error}</Alert>;

  return (
    <>
      <Alert type="info">
        Informative mode: study the profiles and the adaptation process. Working on a real customer
        happens in <Link to="/master/eng" style={{ textDecoration: 'underline' }}>Project Engagement</Link>.
      </Alert>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 14, margin: '18px 0' }}>
        {state.profiles.map(p => (
          <Card key={p.name} style={{ padding: '20px 22px' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.05rem', marginBottom: 6 }}>{p.name}</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--ink-faint)', marginBottom: 12 }}>
              4 dimensions · {new Set(p.facets.map(f => f.pillar)).size} pillars · {p.facets.length} facets
            </div>
            <Btn size="sm" variant="outline" onClick={() => setOpen(p)}>Open full profile</Btn>
          </Card>
        ))}
      </div>
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 10 }}>How adaptation works</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
        {['Every facet is reviewed — adaptation is the exception (typically 3–7 overrides)',
          'Overrides add customer context; they never redefine what a facet measures',
          'Target levels (1.0–5.0) are optional',
          'Adapted profiles (built for a specific customer) are authored and approved by any Ice Pilot for the engagement; sharing a profile or its new facets into the HB library needs the Adapted Profile Owner’s approval',
          'Signature profiles — the standard, versioned products — are created, versioned and retired only by the Signature Profile Owner'].map(t => (
          <Card key={t} style={{ padding: '12px 16px', fontSize: '0.86rem', color: 'var(--ink-soft)' }}>▪ {t}</Card>
        ))}
      </div>
      {open && (
        <Modal open onClose={() => setOpen(null)} title={open.name}>
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {[...new Set(open.facets.map(f => f.dimension))].map(dim => (
              <div key={dim} style={{ marginBottom: 14 }}>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, borderTop: '2px solid var(--ink)', paddingTop: 8 }}>{dim}</div>
                {[...new Set(open.facets.filter(f => f.dimension === dim).map(f => f.pillar))].map(pl => (
                  <div key={pl} style={{ margin: '8px 0' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 700 }}>{pl}</div>
                    {open.facets.filter(f => f.dimension === dim && f.pillar === pl).map(f => (
                      <div key={f.name} style={{ fontSize: '0.82rem', padding: '4px 0', borderBottom: '1px solid var(--line)' }}>
                        <b>{f.name}</b>
                        {f.definition && <div style={{ color: 'var(--ink-faint)', fontSize: '0.76rem' }}>{f.definition}</div>}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Modal>
      )}
    </>
  );
}

// ── Project Engagement ────────────────────────────────────────────────────
// NOTE: superseded by the role-track flow (Needs → Design). Kept for M4, which
// will rebuild programme composition on the role-track profile.
// eslint-disable-next-line no-unused-vars
function Engagement({ embedded = false }) {
  const [projectId, setProjectId] = useEngagement();
  const { loading, error, data, reload } = useEngagementData(projectId);
  const [projects, setProjects] = useState(null);
  const [sigProfiles, setSigProfiles] = useState([]);
  const [details, setDetails] = useState(false);
  const [busy, setBusy] = useState(false);
  const [intakeAi, setIntakeAi] = useState(null);   // { loading, draft, error }
  const navigate = useNavigate();

  useEffect(() => {
    if (!projectId) {
      api.explore.getProjects().then(d => setProjects(d.projects)).catch(e => setProjects({ error: e.message }));
    }
    api.master.getSignatureProfiles().then(d => setSigProfiles(d.profiles)).catch(() => {});
  }, [projectId]);

  async function runIntake() {
    setIntakeAi({ loading: true });
    try { const r = await api.ai.intake(projectId); setIntakeAi({ draft: r.draft }); }
    catch (e) { setIntakeAi({ error: e.message }); }
  }

  // Bez projekta: izbor projekta
  if (!projectId) {
    if (embedded) return null; // Design's own picker/message handles the no-project case
    return (
      <>
        <Alert type="info">
          Picking and adapting a profile is always done for a concrete project. Select one from your
          CRM — or create it first in <Link to="/explore/crm" style={{ textDecoration: 'underline' }}>Explore → My Projects</Link>.
        </Alert>
        {!projects ? <Loading /> : projects.error ? <Alert type="error">{projects.error}</Alert> : (
          <div style={{ display: 'grid', gap: 10, marginTop: 18, maxWidth: 640 }}>
            {projects.map(p => (
              <Card key={p.id} hover onClick={() => setProjectId(p.id)} style={{ padding: '14px 18px' }}>
                <b>{p.name}</b>
                <span style={{ color: 'var(--ink-faint)', fontSize: '0.85rem' }}> · {p.customer} · {p.stage}</span>
              </Card>
            ))}
            {!projects.length && <Alert type="warn">Nemaš nijedan projekat — kreiraj ga u Explore → My Projects.</Alert>}
          </div>
        )}
        {/* Notes & tools — vidljivo i bez izabranog projekta */}
        <PhaseNotes phase="M" />
      </>
    );
  }

  if (loading || !data) return <Loading />;
  if (error) return <Alert type="error">{error}</Alert>;

  async function pickProfile(name) {
    setBusy(true);
    try { await api.master.createProfile(projectId, { baseProfile: name }); reload(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  async function confirmSuggested() {
    setBusy(true);
    try { await api.master.approveProfile(data.profile.id, true); navigate('/master/builder'); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  async function setMode(facetId, mode) {
    try { await api.master.updateFacets(data.profile.id, [{ facetId, mode }]); reload(); }
    catch (e) { alert(e.message); }
  }
  async function setTarget(facetId, value) {
    try { await api.master.updateFacets(data.profile.id, [{ facetId, targetLevel: value ? parseFloat(value) : null }]); reload(); }
    catch (e) { alert(e.message); }
  }
  async function toggleReview(dim, reviewed) {
    try { await api.master.reviewDimension(data.profile.id, dim, reviewed); reload(); }
    catch (e) { alert(e.message); }
  }
  async function approveReviewed() {
    setBusy(true);
    try { await api.master.approveProfile(data.profile.id, false); navigate('/master/builder'); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }

  const p = data.profile;
  const dims = p ? [...new Set(p.facets.map(f => f.dimension))] : [];

  return (
    <>
    {/* Prebačena sekcija "Notes & tools" iz PhaseOverview (deljena komponenta) */}
      {!embedded && <PhaseNotes phase="M" />}
      {!embedded && <EngagementBanner data={data} onClose={() => setProjectId(null)} />}

      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', margin: '20px 0 8px' }}>Step 1: customer needs — the intake</h3>
      <p style={{ fontSize: '0.86rem', color: 'var(--ink-soft)', marginBottom: 12 }}>
        Carry the intake from your consultative conversations — or let the assistant draft an engagement
        brief, party map and follow-up diagnostic questions from what the project already knows.
      </p>
      <Btn size="sm" variant="outline" onClick={runIntake} style={{ marginBottom: 20 }}>
        Synthesise intake with AI
      </Btn>
      {intakeAi && (
        <Modal open onClose={() => setIntakeAi(null)} title="AI intake synthesis">
          {intakeAi.loading ? (
            <div style={{ textAlign: 'center', padding: '30px 0' }}>
              <Spinner /><p style={{ marginTop: 12, fontSize: '0.85rem', color: 'var(--ink-soft)' }}>Claude is synthesising the engagement brief…</p>
            </div>
          ) : intakeAi.error ? <Alert type="error">{intakeAi.error}</Alert> : (
            <div>
              <Alert type="info">
                Draft attached to an approval — review it in{' '}
                <Link to="/contribute/myapprovals" style={{ textDecoration: 'underline' }}>Contribute → My Approvals</Link>.
              </Alert>
              <pre style={{
                marginTop: 12, padding: '12px 14px', background: 'var(--canvas-warm)', borderRadius: 'var(--radius-md)',
                fontSize: '0.8rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', fontFamily: 'var(--font-body)',
                maxHeight: 360, overflowY: 'auto',
              }}>{intakeAi.draft}</pre>
            </div>
          )}
        </Modal>
      )}

      {!p ? (
        <>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 10 }}>
            Step 2: pick the baseline profile for {data.project.name}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
            {sigProfiles.map(sp => (
              <Card key={sp.name} style={{ padding: '18px 20px' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 6 }}>{sp.name}</div>
                <div style={{ fontSize: '0.76rem', color: 'var(--ink-faint)', marginBottom: 10 }}>Signature profile · {sp.facets.length} facets</div>
                <Btn size="sm" loading={busy} onClick={() => pickProfile(sp.name)}>Select as primary</Btn>
              </Card>
            ))}
          </div>
        </>
      ) : p.status === 'approved' ? (
        <Alert type="success">
          Ideal Profile approved ({p.baseProfile}, {p.facets.length} facets) — saved as HB property for
          future reference. Continue in the <Link to="/master/builder" style={{ textDecoration: 'underline' }}>Programme Builder →</Link>
        </Alert>
      ) : !details ? (
        <>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 8 }}>
            Step 3: the suggested Ideal Profile for {data.project.name}
          </h3>
          <Alert type="info">
            <b>This is our strong suggestion — confirm it, or go into the details.</b> Built on {p.baseProfile}:{' '}
            <b>{p.facets.length} facets</b> across 4 dimensions and {new Set(p.facets.map(f => f.pillar)).size} pillars,{' '}
            {p.facets.filter(f => f.mandatory).length} mandatory (🔒 protected). Structure is protected either way.
          </Alert>
          <div style={{ display: 'flex', gap: 10, margin: '14px 0 20px', flexWrap: 'wrap' }}>
            <Btn loading={busy} onClick={confirmSuggested}>Confirm Ideal Profile as suggested → programme</Btn>
            <Btn variant="outline" onClick={() => setDetails(true)}>Go into the details (advanced)</Btn>
          </div>
          {dims.map(dim => (
            <div key={dim} style={{ marginBottom: 10 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, borderTop: '2px solid var(--ink)', paddingTop: 8 }}>{dim}</div>
              {[...new Set(p.facets.filter(f => f.dimension === dim).map(f => f.pillar))].map(pl => (
                <div key={pl} style={{ margin: '6px 0' }}>
                  <b style={{ fontSize: '0.85rem' }}>{pl}</b>
                  <span style={{ color: 'var(--ink-faint)', fontSize: '0.78rem' }}> ({p.facets.filter(f => f.pillar === pl).length} facets)</span>
                  <div style={{ fontSize: '0.82rem', color: 'var(--ink-soft)' }}>
                    {p.facets.filter(f => f.dimension === dim && f.pillar === pl).map(f => (
                      <span key={f.id} style={{ marginRight: 12 }}>{f.mandatory ? '🔒 ' : ''}{f.name}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </>
      ) : (
        <>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 8 }}>
            Step 3 · details: review facet by facet
          </h3>
          <Alert type="info">
            Advanced mode — 🔒 mandatory facets are protected against de-emphasis. Mark each dimension
            reviewed to approve. <button onClick={() => setDetails(false)} style={{ textDecoration: 'underline', color: 'inherit' }}>← back to the suggestion</button>
          </Alert>
          {dims.map(dim => (
            <div key={dim} style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '2px solid var(--ink)', paddingTop: 8, marginTop: 10 }}>
                <b style={{ fontFamily: 'var(--font-display)' }}>{dim}</b>
                <label style={{ fontSize: '0.8rem', color: 'var(--ink-soft)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={p.reviewedDimensions.includes(dim)}
                    onChange={e => toggleReview(dim, e.target.checked)}
                    style={{ marginRight: 6 }}
                  />
                  dimension reviewed
                </label>
              </div>
              {p.facets.filter(f => f.dimension === dim).map(f => (
                <div key={f.id} style={{ display: 'flex', gap: 8, alignItems: 'center', borderBottom: '1px solid var(--line)', padding: '5px 0', flexWrap: 'wrap' }}>
                  <span style={{ flex: 1, fontSize: '0.85rem', minWidth: 180 }}>
                    {f.mandatory ? '🔒 ' : ''}{f.name}
                    <span style={{ color: 'var(--ink-faint)', fontSize: '0.72rem' }}> · {f.pillar}</span>
                  </span>
                  <Select value={f.mode} onChange={e => setMode(f.id, e.target.value)} style={{ maxWidth: 150, padding: '4px 8px', fontSize: '0.8rem' }}>
                    <option value="baseline">baseline</option>
                    <option value="emphasised">emphasised</option>
                    <option value="de-emphasised" disabled={f.mandatory}>de-emphasised</option>
                  </Select>
                  <Select value={f.targetLevel ?? ''} onChange={e => setTarget(f.id, e.target.value)} style={{ maxWidth: 90, padding: '4px 8px', fontSize: '0.8rem' }}>
                    <option value="">target —</option>
                    {['1.0', '1.5', '2.0', '2.5', '3.0', '3.5', '4.0', '4.5', '5.0'].map(l => <option key={l} value={l}>{l}</option>)}
                  </Select>
                </div>
              ))}
            </div>
          ))}
          <Btn loading={busy} onClick={approveReviewed} disabled={p.reviewedDimensions.length < 4}>
            Approve Ideal Profile ({p.reviewedDimensions.length}/4 dimensions reviewed)
          </Btn>
        </>
      )}
      
    </>
  );
}

// ── Programme Builder ─────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
function Builder({ embedded = false }) {
  const [projectId, setProjectId] = useEngagement();
  const { loading, error, data, reload } = useEngagementData(projectId);
  const [matching, setMatching] = useState(null);
  const [days, setDays] = useState({ 1: [], 2: [], 3: [], 4: [] });
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [ai, setAi] = useState(null);   // { loading, title, draft, error }
  const navigate = useNavigate();

  useEffect(() => {
    if (data?.profile?.status === 'approved') {
      api.master.getMatching(data.profile.id)
        .then(setMatching)
        .catch(e => setMatching({ error: e.message }));
      if (!name) setName(`${data.project.customer} — tailor-made programme`);
    }
    // eslint-disable-next-line
  }, [data?.profile?.id, data?.profile?.status]);

  if (!projectId) return <Alert type="info">Start a <Link to="/master/eng" style={{ textDecoration: 'underline' }}>Project Engagement</Link> first — the programme is always built for a concrete customer.</Alert>;
  if (loading || !data) return <Loading />;
  if (error) return <Alert type="error">{error}</Alert>;
  if (!data.profile || data.profile.status !== 'approved')
    return <Alert type="info">Pick and approve a profile for {data.project.name} — <Link to="/master/eng" style={{ textDecoration: 'underline' }}>Project Engagement</Link>.</Alert>;

  function toggleUnit(day, code) {
    setDays(d => ({
      ...d,
      [day]: d[day].includes(code) ? d[day].filter(c => c !== code) : [...d[day], code],
    }));
  }
  async function adaptForClient(code, name) {
    setAi({ loading: true, title: `Adapt (${code}) ${name}` });
    try {
      const r = await api.ai.adaptUnit(projectId, code);
      setAi({ title: `Adapt (${code}) ${name}`, draft: r.draft });
    } catch (e) { setAi({ title: `Adapt (${code})`, error: e.message }); }
  }
  async function draftNewUnit() {
    setAi({ loading: true, title: 'Draft new Unit with AI' });
    try {
      const r = await api.ai.draftUnit(projectId, matching.gaps);
      setAi({ title: 'New Unit draft', draft: r.draft });
    } catch (e) { setAi({ title: 'New Unit draft', error: e.message }); }
  }
  async function save() {
    const dayList = Object.entries(days)
      .filter(([, units]) => units.length)
      .map(([day, unitCodes]) => ({ day: parseInt(day, 10), unitCodes }));
    if (!dayList.length) { alert('Dodeli bar jedan Unit nekom danu.'); return; }
    setBusy(true);
    try {
      await api.master.saveProgramme(projectId, { name, days: dayList });
      reload();
      navigate('/master/proposals');
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  }

  return (
    <>
      {!embedded && <EngagementBanner data={data} onClose={() => setProjectId(null)} />}
      {embedded && <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', margin: '28px 0 4px' }}>Programme</h3>}
      {data.programme && (
        <Alert type="success">
          Programme "{data.programme.name}" saved ({data.programme.days.length} slots). Continue to{' '}
          <Link to="/master/proposals" style={{ textDecoration: 'underline' }}>Proposals →</Link>
        </Alert>
      )}
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', margin: '18px 0 8px' }}>
        Matched from Unit records ({data.profile.baseProfile})
      </h3>
      <Alert type="info">
        <b>Co-created in steps, not one shot:</b> matched Units → days → adaptations → materials →
        proposal. Score = Σ tag weight × adaptation multiplier (emphasised ×2, de-emphasised ×0).
      </Alert>
      {!matching ? <Loading /> : matching.error ? <Alert type="error">{matching.error}</Alert> : (
        <>
          <Card style={{ padding: '10px 18px', margin: '14px 0' }}>
            <Table
              headers={['#', 'Unit', 'Score', 'Matching facets', 'Add to day', 'AI']}
              rows={matching.units.slice(0, 12).map((x, i) => [
                i + 1,
                <Link to={`/deliver/units/${x.code}`} style={{ textDecoration: 'underline' }}>({x.code}) {x.name}</Link>,
                <b>{x.score}</b>,
                <span style={{ fontSize: '0.78rem' }}>{x.hits.slice(0, 4).join(', ')}</span>,
                <span style={{ display: 'flex', gap: 4 }}>
                  {[1, 2, 3, 4].map(d => (
                    <button
                      key={d}
                      onClick={() => toggleUnit(d, x.code)}
                      style={{
                        width: 26, height: 26, borderRadius: 4, fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
                        border: '1px solid var(--ink-faint)',
                        background: days[d].includes(x.code) ? 'var(--ink)' : 'transparent',
                        color: days[d].includes(x.code) ? '#fff' : 'var(--ink-soft)',
                      }}
                    >{d}</button>
                  ))}
                </span>,
                <Btn size="sm" variant="outline" onClick={() => adaptForClient(x.code, x.name)}>Adapt for client</Btn>,
              ])}
              emptyMessage="No matching Units — adapt the profile or check that Unit facet tags are seeded"
            />
          </Card>
          {matching.gaps.length > 0 && (
            <Alert type="warn">
              <b>Gaps:</b> no existing Unit develops: {matching.gaps.join(', ')}.{' '}
              <Btn size="sm" onClick={draftNewUnit} style={{ marginLeft: 8 }}>Draft new Unit with AI</Btn>
            </Alert>
          )}
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', margin: '20px 0 8px' }}>Draft day schedule (3–4 week intervals)</h3>
          <Card style={{ padding: '10px 18px', marginBottom: 16 }}>
            <Table
              headers={['Day', 'Units', 'Between days']}
              rows={[1, 2, 3, 4].filter(d => days[d].length).map(d => [
                `Day ${d}`,
                days[d].map(code => {
                  const u = matching.units.find(x => x.code === code);
                  return `(${code}) ${u ? u.name : ''}`;
                }).join(' · '),
                'homework · memory cards · Expedition Log',
              ])}
              emptyMessage="Klikni na broj dana pored Unit-a da ga rasporediš"
            />
          </Card>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Programme name" style={{ maxWidth: 340 }} />
            <Btn loading={busy} onClick={save}>Save programme → Proposals</Btn>
          </div>
        </>
      )}
      {ai && (
        <Modal open onClose={() => setAi(null)} title={ai.title}>
          {ai.loading ? (
            <div style={{ textAlign: 'center', padding: '30px 0' }}>
              <Spinner />
              <p style={{ marginTop: 12, fontSize: '0.85rem', color: 'var(--ink-soft)' }}>
                Claude is drafting — participant-facing content only, never the craft…
              </p>
            </div>
          ) : ai.error ? (
            <Alert type="error">{ai.error}</Alert>
          ) : (
            <div>
              <Alert type="info">
                This is a <b>draft</b> — it has been attached to an approval. Approve it in{' '}
                <Link to="/contribute/myapprovals" style={{ textDecoration: 'underline' }}>Contribute → My Approvals</Link>{' '}
                and it becomes HB property, credited to you.
              </Alert>
              <pre style={{
                marginTop: 12, padding: '12px 14px', background: 'var(--canvas-warm)', borderRadius: 'var(--radius-md)',
                fontSize: '0.8rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', fontFamily: 'var(--font-body)',
                maxHeight: 360, overflowY: 'auto',
              }}>{ai.draft}</pre>
            </div>
          )}
        </Modal>
      )}
    </>
  );
}

// ── Proposals + closing ───────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL || 'https://api.hansenbeck.com';

// Panel: priprema + generisanje HB proposal .pptx dokumenta iz record podataka.
function DocGenPanel({ proposal, onReload }) {
  const [form, setForm] = useState({
    situation: proposal.situation || '',
    workshopDays: proposal.pricing?.workshopDays ?? 5,
    workshopRate: proposal.pricing?.workshopRate ?? 4000,
    virtualUnits: proposal.pricing?.virtualUnits ?? 1,
    virtualRate: proposal.pricing?.virtualRate ?? 420,
    currency: proposal.pricing?.currency || 'EUR',
  });
  const [busy, setBusy] = useState(false);

  async function generate() {
    setBusy(true);
    try {
      await api.master.updateProposalDetails(proposal.id, {
        situation: form.situation,
        workshopDays: Number(form.workshopDays), workshopRate: Number(form.workshopRate),
        virtualUnits: Number(form.virtualUnits), virtualRate: Number(form.virtualRate),
        currency: form.currency,
      });
      await api.master.generateProposalDoc(proposal.id);
      onReload();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  }

  const num = (k, label, w = 90) => (
    <label style={{ fontSize: '0.78rem', color: 'var(--ink-soft)' }}>
      {label}<br />
      <Input type="number" value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} style={{ width: w, marginTop: 3 }} />
    </label>
  );

  return (
    <Card style={{ padding: '16px 20px', marginTop: 12, background: 'var(--canvas-warm)' }}>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 4 }}>Prepare proposal document</div>
      <p style={{ fontSize: '0.8rem', color: 'var(--ink-faint)', marginBottom: 12 }}>
        Customer, objectives (from the approved ideal profile), programme content and trainer fill
        automatically from the records. Add the situation and pricing below.
      </p>
      <label style={{ fontSize: '0.78rem', color: 'var(--ink-soft)' }}>
        Situation (customer context)
        <textarea
          value={form.situation}
          onChange={e => setForm(f => ({ ...f, situation: e.target.value }))}
          placeholder="Leave empty to use the Needs Summary — the customer's ambition, people, timeline and their own words…"
          style={{ width: '100%', minHeight: 70, marginTop: 4, padding: '8px 10px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--canvas-white)', fontFamily: 'var(--font-body)', fontSize: '0.88rem', resize: 'vertical' }}
        />
      </label>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 12, alignItems: 'flex-end' }}>
        {num('workshopDays', 'Workshop days', 80)}
        {num('workshopRate', 'Price / day')}
        {num('virtualUnits', 'Virtual units', 80)}
        {num('virtualRate', 'Price / virtual')}
        <label style={{ fontSize: '0.78rem', color: 'var(--ink-soft)' }}>
          Currency<br />
          <Input value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))} style={{ width: 70, marginTop: 3 }} />
        </label>
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
        <Btn loading={busy} onClick={generate}>
          {proposal.documentUrl ? 'Regenerate document' : 'Generate proposal document'}
        </Btn>
        {proposal.documentUrl && (
          <a href={`${API_BASE}${proposal.documentUrl}`} target="_blank" rel="noreferrer"
             style={{ fontSize: '0.88rem', fontWeight: 600, textDecoration: 'underline' }}>
            ↓ Download .pptx
          </a>
        )}
      </div>
    </Card>
  );
}

// eslint-disable-next-line no-unused-vars
function Proposals() {
  const [projectId, setProjectId] = useEngagement();
  const { loading, error, data, reload } = useEngagementData(projectId);
  const [busy, setBusy] = useState(false);
  const [openDoc, setOpenDoc] = useState(null);

  if (!projectId) return <Alert type="info">Start a <Link to="/master/eng" style={{ textDecoration: 'underline' }}>Project Engagement</Link> first.</Alert>;
  if (loading || !data) return <Loading />;
  if (error) return <Alert type="error">{error}</Alert>;

  async function createDraft() {
    setBusy(true);
    try { await api.master.createProposal(projectId); reload(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  async function setStatus(id, status) {
    try { await api.master.updateProposal(id, status); reload(); }
    catch (e) { alert(e.message); }
  }
  async function close() {
    setBusy(true);
    try {
      const r = await api.master.closeEngagement(projectId);
      alert(`Agreement reached — engagement moved to Deliver (${r.deliveriesCreated} delivery days created). Graduation to the HB Clients registry proposed in Explore → My Projects.`);
      reload();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  }

  const statusBadge = { draft: 'pending', sent: 'active', accepted: 'completed', declined: 'cancelled' };
  const openProposal = data.proposals.find(pr => pr.id === openDoc);

  return (
    <>
      <EngagementBanner data={data} onClose={() => setProjectId(null)} />
      <Alert type="info">
        The proposal is the first and only place our content appears in customer value language —
        rendered from the records into the HB proposal template (PPT).
      </Alert>
      <Card style={{ padding: '10px 18px', margin: '16px 0' }}>
        <Table
          headers={['#', 'Status', 'Document', 'Created', 'Actions']}
          rows={data.proposals.map(pr => [
            pr.id,
            <Badge status={statusBadge[pr.status] || 'default'}>{pr.status}</Badge>,
            pr.documentUrl
              ? <a href={`${API_BASE}${pr.documentUrl}`} target="_blank" rel="noreferrer" style={{ textDecoration: 'underline', fontSize: '0.82rem' }}>↓ .pptx</a>
              : <span style={{ color: 'var(--ink-faint)', fontSize: '0.82rem' }}>—</span>,
            new Date(pr.createdAt).toLocaleDateString(),
            <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <Btn size="sm" variant={openDoc === pr.id ? 'primary' : 'outline'} onClick={() => setOpenDoc(openDoc === pr.id ? null : pr.id)}>
                {pr.documentUrl ? 'Document' : 'Generate doc'}
              </Btn>
              {pr.status === 'draft' && <Btn size="sm" variant="outline" onClick={() => setStatus(pr.id, 'sent')}>Mark sent</Btn>}
              {pr.status === 'sent' && <Btn size="sm" variant="outline" onClick={() => setStatus(pr.id, 'declined')}>Declined</Btn>}
            </span>,
          ])}
          emptyMessage="No proposals yet"
        />
      </Card>
      {openProposal && <DocGenPanel proposal={openProposal} onReload={reload} />}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Btn variant="outline" loading={busy} onClick={createDraft} disabled={!data.programme}>
          Create proposal draft{!data.programme ? ' (build the programme first)' : ''}
        </Btn>
        <Btn loading={busy} onClick={close} disabled={!data.programme || !!data.project.closedWonDate}>
          {data.project.closedWonDate ? 'Engagement closed ✓' : 'Agreement reached — close & move to Deliver'}
        </Btn>
      </div>
      {data.deliveries.length > 0 && (
        <Card style={{ padding: '16px 20px', marginTop: 18 }}>
          <b style={{ fontFamily: 'var(--font-display)' }}>Delivery days created</b>
          <div style={{ fontSize: '0.85rem', color: 'var(--ink-soft)', marginTop: 6 }}>
            {data.deliveries.map(d => `Day ${d.day} (${d.status})`).join(' · ')} — see Deliver → My Client Deliveries (Phase 3C).
          </div>
        </Card>
      )}
      
    </>
  );
}

// ── YPL referenca ─────────────────────────────────────────────────────────
function Ypl() {
  const steps = [
    ['1 · The project, not a customer', 'YPL is a project with many parties — sponsors, country partners, participants, mentors — carried in the project-level CRM. Sponsor input was gathered through a structured form.'],
    ['2 · Full adaptation of a signature profile', 'Inspiring Leadership 1.0 → the tailor-made YPL Ideal Profile: 40 capabilities, pillar recast as "Towards Position & Democracy", HEXACO before Day 1, methodology v7.3.2.'],
    ['3 · The 6-day programme', 'Who am I as a democratic leader → Developing others and listening deeply → Influence, negotiation and difficult conversations → The leader\'s hardest conversations → Democratic influence: vision, ethics and mobilisation → Integration, legacy and continuing development.'],
    ['4 · Adaptation of existing Units', 'The change table (MODIFY / REPLACE scenario / KEEP): e.g. ML003 Motivation keeps all theory and replaces the practice scenario; role-plays recast for political reality.'],
    ['5 · Three new Units for the gap', '(ML-YPL-01) Horizontal Leadership, (ML-YPL-02) Ethics as Active Force, (ML-YPL-03) Democratic Vision Communication — no equivalent in any HB programme; now HB property.'],
    ['6 · Documents for every party', 'Sponsor document and deck, stakeholder set, programme website, participant personal files, assessment instruments per methodology v7.3.x, regional variants — all generated from the same records.'],
  ];
  return (
    <>
      <Alert type="info">
        The golden thread: every step below really happened, with real deliverables — the acceptance
        test for Basecamp. Six countries: Colombia, Gambia, Lebanon, the Philippines, Serbia, Zambia.
      </Alert>
      <div style={{ display: 'grid', gap: 12, marginTop: 16 }}>
        {steps.map(([title, text]) => (
          <Card key={title} style={{ padding: '16px 20px' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 6 }}>{title}</div>
            <div style={{ fontSize: '0.86rem', color: 'var(--ink-soft)', lineHeight: 1.6 }}>{text}</div>
          </Card>
        ))}
      </div>
      <p style={{ marginTop: 14, fontSize: '0.8rem', color: 'var(--ink-faint)' }}>
        Where Claude acts along the thread (intake synthesis, adaptation proposal, programme
        composition, Unit adaptation, new Units, party documents) — AI capabilities arrive in Phase 3C;
        every output is a draft attached to an approval, never published on its own.
      </p>
    </>
  );
}

// ── Master area ───────────────────────────────────────────────────────────
// ── Needs (M2) ──────────────────────────────────────────────────────────────
// The first Master screen: open a role track for a project, capture the need
// (write / attach), synthesise a draft, edit the summary, confirm Gate 1 (which
// advances the CRM stage to Needs Assessment). Design & Proposal come after.
const NEEDS_FIELDS = [
  ['ambition', 'Ambition — the business goal, in their words', true],
  ['roleAndLevel', 'Role & level', false],
  ['population', 'Population', false],
  ['timeline', 'Timeline', false],
  ['clientVocabulary', 'Client vocabulary — their own framework, verbatim', true],
  ['language', 'Language', false],
  ['constraints', 'Constraints', true],
  ['decisionPath', 'Decision path — who decides, on what timeline, against what budget', true],
];
const taStyle = { width: '100%', fontFamily: 'var(--font-body)', fontSize: '0.9rem', lineHeight: 1.5, padding: '10px 13px', borderRadius: 'var(--radius-md)', border: '1px solid var(--line)', resize: 'vertical', outline: 'none' };
const fieldLabel = { fontSize: '0.8rem', fontWeight: 500, color: 'var(--ink-soft)', display: 'block', marginBottom: 5 };
// Navigation styled as the primary button (it goes somewhere, so it stays a link).
const primaryLinkStyle = { display: 'inline-block', background: 'var(--ink)', color: '#fff', padding: '9px 18px', borderRadius: 'var(--radius-md)', fontSize: '0.88rem', fontWeight: 500, textDecoration: 'none' };
// Compact "you are here" line on top of an embedded step (customer · role track · stage).
const contextBarStyle = { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: '0.84rem', color: 'var(--ink-soft)', background: 'var(--canvas-warm)', borderRadius: 'var(--radius-md)', padding: '8px 14px', marginBottom: 16 };

export function Needs() {
  const [projectId, setProjectId] = useEngagement();
  const [projects, setProjects] = useState(null);
  const [tracks, setTracks] = useState(null);
  const [trackId, setTrackId] = useRoleTrack();
  const [state, setState] = useState({ loading: false, data: null, error: null });
  const [newRole, setNewRole] = useState({ roleName: '', level: '', population: '' });
  const [intake, setIntake] = useState({ title: '', payload: '' });
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ai, setAi] = useState(null);

  useEffect(() => {
    if (!projectId) api.explore.getProjects().then(d => setProjects(d.projects)).catch(e => setProjects({ error: e.message }));
  }, [projectId]);
  const loadTracks = useCallback(() => {
    if (!projectId) return;
    api.master.getRoleTracks(projectId).then(d => setTracks(d.roleTracks)).catch(e => setTracks({ error: e.message }));
  }, [projectId]);
  useEffect(() => { loadTracks(); }, [loadTracks]);
  const loadTrack = useCallback(() => {
    if (!trackId) { setState({ loading: false, data: null, error: null }); setForm(null); return; }
    setState(s => ({ ...s, loading: true }));
    api.master.getRoleTrack(trackId)
      .then(d => { setState({ loading: false, data: d, error: null }); setForm({ ...d.needs, roleOrientation: d.needs?.roleOrientation || '' }); })
      .catch(e => setState({ loading: false, data: null, error: e.message }));
  }, [trackId]);
  useEffect(() => { loadTrack(); }, [loadTrack]);

  // 1) No project — pick one (shares the engagement project with Design/Proposal)
  if (!projectId) {
    return (
      <>
        <Alert type="info">
          Everything starts on a project. Pick one from your CRM, or create it in{' '}
          <Link to="/explore/crm" style={{ textDecoration: 'underline' }}>Explore → My Projects</Link>.
        </Alert>
        {!projects ? <Loading /> : projects.error ? <Alert type="error">{projects.error}</Alert> : (
          <div style={{ display: 'grid', gap: 10, marginTop: 18, maxWidth: 640 }}>
            {projects.map(p => (
              <Card key={p.id} hover onClick={() => setProjectId(p.id)} style={{ padding: '14px 18px' }}>
                <b>{p.name}</b><span style={{ color: 'var(--ink-faint)', fontSize: '0.85rem' }}> · {p.customer} · {p.stage}</span>
              </Card>
            ))}
            {!projects.length && <Alert type="warn">No projects yet — create one in Explore → My Projects.</Alert>}
          </div>
        )}
        <PhaseNotes phase="M" />
      </>
    );
  }

  async function createTrack(e) {
    e.preventDefault();
    if (!newRole.roleName.trim()) return;
    setBusy(true);
    try { const r = await api.master.createRoleTrack(projectId, newRole); setNewRole({ roleName: '', level: '', population: '' }); loadTracks(); setTrackId(r.roleTrackId); notifyWorkspace(); }
    catch (err) { alert(err.message); } finally { setBusy(false); }
  }

  // 2) Project chosen, no track selected — list / create role tracks
  if (!trackId) {
    return (
      <>
        <Alert type="info">
          A project carries one or more <b>role tracks</b> — one per population you develop (Japan SI: system
          engineers now, sales later). Open one to capture its need.
          <button onClick={() => setProjectId(null)} style={{ marginLeft: 12, textDecoration: 'underline', fontSize: '0.82rem', color: 'inherit' }}>Change project</button>
        </Alert>
        {tracks === null ? <Loading /> : tracks.error ? <Alert type="error">{tracks.error}</Alert> : (
          <div style={{ display: 'grid', gap: 10, margin: '18px 0', maxWidth: 680 }}>
            {tracks.map(t => (
              <Card key={t.id} hover onClick={() => setTrackId(t.id)} style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div><b>{t.roleName}</b>{t.level ? <span style={{ color: 'var(--ink-faint)', fontSize: '0.85rem' }}> · {t.level}</span> : null}{t.population ? <span style={{ color: 'var(--ink-faint)', fontSize: '0.85rem' }}> · {t.population}</span> : null}</div>
                <Badge status={t.status === 'needs_confirmed' ? 'active' : 'default'} >{t.status.replace('_', ' ')}</Badge>
              </Card>
            ))}
            {!tracks.length && <p style={{ fontSize: '0.86rem', color: 'var(--ink-faint)' }}>No role tracks yet — open the first one below.</p>}
          </div>
        )}
        <Card style={{ padding: '16px 20px', maxWidth: 680 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', marginBottom: 10 }}>Open a role track</h3>
          <form onSubmit={createTrack} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Input placeholder="Role (e.g. Upstream System Engineer)" value={newRole.roleName} onChange={e => setNewRole({ ...newRole, roleName: e.target.value })} />
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Input placeholder="Level (e.g. Section Manager)" value={newRole.level} onChange={e => setNewRole({ ...newRole, level: e.target.value })} style={{ flex: 1, minWidth: 160 }} />
              <Input placeholder="Population (e.g. ~200)" value={newRole.population} onChange={e => setNewRole({ ...newRole, population: e.target.value })} style={{ flex: 1, minWidth: 140 }} />
            </div>
            <Btn type="submit" loading={busy} style={{ alignSelf: 'flex-start' }}>Open role track</Btn>
          </form>
        </Card>
      </>
    );
  }

  // 3) Track detail — intake, synthesise, needs fields, Gate 1
  const d = state.data;
  if (state.error) return <Alert type="error">{state.error}</Alert>;
  if (state.loading || !d || !form) return <Loading />;
  const gate1 = d.gates.find(g => g.step === 1);
  const locked = !!d.gates.find(g => g.step >= 2);
  const setField = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  async function addIntake() {
    if (!intake.payload.trim()) return;
    setBusy(true);
    try { await api.master.addIntake(trackId, { kind: 'text', title: intake.title || null, payload: intake.payload }); setIntake({ title: '', payload: '' }); loadTrack(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  async function removeIntake(id) {
    try { await api.master.deleteIntake(trackId, id); loadTrack(); } catch (e) { alert(e.message); }
  }
  async function uploadFile(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setBusy(true);
    try { await api.master.uploadIntakeFile(trackId, file); loadTrack(); }
    catch (err) { alert(err.message); } finally { setBusy(false); e.target.value = ''; }
  }
  async function synthesise() {
    setAi({ loading: true });
    try { const r = await api.master.synthesiseNeeds(trackId); setAi({ draft: r.draft }); }
    catch (e) { setAi({ error: e.message }); }
  }
  async function saveNeeds() {
    setBusy(true);
    try { await api.master.updateNeeds(trackId, form); loadTrack(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  async function toggleGate1() {
    setBusy(true);
    try {
      if (gate1) await api.master.reverseGate(trackId, 1);
      else await api.master.confirmGate(trackId, 1);
      loadTrack(); loadTracks(); notifyWorkspace();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  }

  return (
    <>
      <div style={contextBarStyle}>
        <span><b>{d.project.customer}</b> · {d.roleTrack.roleName}{d.roleTrack.level ? ` (${d.roleTrack.level})` : ''} · {d.project.stage}</span>
        <Btn variant="link" onClick={() => setTrackId(null)} style={{ marginLeft: 'auto', fontSize: '0.82rem', color: 'var(--ink-soft)' }}>All role tracks</Btn>
      </div>

      {/* Intake */}
      <SectionCard title="Collect the need" hint="Write meeting notes, attach a file the client sent (competence chart, RFP…), or ask the parties (below). Their exact phrasing matters more than a tidy summary.">
        {d.sources.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
            {d.sources.map(s => (
              <div key={s.id} style={{ background: 'var(--canvas-warm)', borderRadius: 'var(--radius-md)', padding: '8px 12px', fontSize: '0.84rem' }}>
                <b>{s.kind}</b>{s.title ? ` · ${s.title}` : ''}
                <button onClick={() => removeIntake(s.id)} style={{ float: 'right', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--danger)' }}>✕</button>
                {s.payload && <div style={{ color: 'var(--ink-soft)', marginTop: 4, whiteSpace: 'pre-wrap' }}>{s.payload}</div>}
                {s.hasFile && (
                  <button onClick={() => api.master.downloadIntakeFile(s.id, s.fileName).catch(e => alert(e.message))}
                    style={{ display: 'block', marginTop: 4, border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: 'var(--ink)', textDecoration: 'underline', fontSize: '0.82rem', fontFamily: 'var(--font-body)' }}>
                    ↓ {s.fileName}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {!locked && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Input placeholder="Source title (optional)" value={intake.title} onChange={e => setIntake({ ...intake, title: e.target.value })} />
            <textarea placeholder="Paste notes / their words…" value={intake.payload} onChange={e => setIntake({ ...intake, payload: e.target.value })} rows={4} style={taStyle} />
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <Btn size="sm" loading={busy} onClick={addIntake}>Add source</Btn>
              <label style={{ fontSize: '0.82rem', color: 'var(--ink)', textDecoration: 'underline', textUnderlineOffset: 2, cursor: 'pointer' }}>
                Attach a file
                <input type="file" onChange={uploadFile} style={{ display: 'none' }} />
              </label>
              <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--ink-faint)' }}>→ drafts the Needs Summary from your intake</span>
                <Btn size="sm" variant="outline" onClick={synthesise}>Summarise what we know</Btn>
              </span>
            </div>
          </div>
        )}
      </SectionCard>

      <AskPanel trackId={trackId} parties={d.parties} locked={locked} />

      {ai && (
        <Modal open onClose={() => setAi(null)} title="Summarise what we know — Needs Summary draft">
          {ai.loading ? <div style={{ textAlign: 'center', padding: '30px 0' }}><Spinner /><p style={{ marginTop: 12, fontSize: '0.85rem', color: 'var(--ink-soft)' }}>Claude is reading the intake…</p></div>
            : ai.error ? <Alert type="error">{ai.error}</Alert> : (
              <div>
                <Alert type="info">AI draft — read it, then edit the summary below freely. Re-running this never overwrites your edits; the draft is offered alongside, never in place of, what you wrote. It is also attached to <Link to="/contribute/myapprovals" style={{ textDecoration: 'underline' }}>Contribute → My Approvals</Link>.</Alert>
                <pre style={{ marginTop: 12, padding: '12px 14px', background: 'var(--canvas-warm)', borderRadius: 'var(--radius-md)', fontSize: '0.8rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', fontFamily: 'var(--font-body)', maxHeight: 360, overflowY: 'auto' }}>{ai.draft}</pre>
              </div>
            )}
        </Modal>
      )}

      {/* Needs summary */}
      <SectionCard title="Needs Summary" hint={!locked ? 'AI draft — edit freely; the summary is yours, the AI only proposes.' : null}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
          {NEEDS_FIELDS.map(([key, label, multi]) => (
            <div key={key} style={multi ? { gridColumn: '1 / -1' } : null}>
              <label style={fieldLabel}>{label}</label>
              {multi
                ? <textarea value={form[key] || ''} onChange={setField(key)} rows={3} disabled={locked} style={taStyle} />
                : <Input value={form[key] || ''} onChange={setField(key)} disabled={locked} />}
            </div>
          ))}
          <div>
            <label style={fieldLabel}>Role orientation</label>
            <Select value={form.roleOrientation || ''} onChange={setField('roleOrientation')} disabled={locked}>
              <option value="">— select —</option>
              {['sales', 'management', 'general', 'technical'].map(o => <option key={o} value={o}>{o}</option>)}
            </Select>
          </div>
        </div>
        {!locked && <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}><Btn loading={busy} onClick={saveNeeds}>Save summary</Btn></div>}
      </SectionCard>

      {/* Gate 1 */}
      <SectionCard title="Gate 1 — confirm the need" tone={gate1 ? 'done' : 'gate'}>
        {gate1 ? (
          <>
            <p style={{ fontSize: '0.86rem', color: 'var(--success)', marginBottom: 12 }}>✓ Confirmed. The project is now at <b>{d.project.stage}</b>.</p>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <ContinueLink to="/master/builder" step="design">Continue to Design →</ContinueLink>
              {!locked && <Btn size="sm" variant="ghost" loading={busy} onClick={toggleGate1}>Reopen (reverse Gate 1)</Btn>}
            </div>
          </>
        ) : (
          <>
            <p style={{ fontSize: '0.86rem', color: 'var(--ink-soft)', marginBottom: 12 }}>A single explicit confirm. Nothing downstream opens until you take it — a profile built on a misread need is worse than none. Reversible until Gate 2.</p>
            <Btn loading={busy} onClick={toggleGate1}>Confirm the need — Gate 1</Btn>
          </>
        )}
      </SectionCard>
    </>
  );
}

// ── Design (M3) — the Ideal Profile, assembled from the facet library ────────
const ORIGIN_BADGE = { inherited: 'default', adapted: 'pending', new: 'active' };
const BASE_PROFILES = ['Inspiring Leadership', 'Modern Employee', 'Sales Excellence'];

// Modal to replace a facet with another from the library (fuzzy search).
function ReplacePicker({ onPick, onClose }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => { api.master.searchFacets(q).then(d => setResults(d.facets)).catch(() => setResults([])); }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <Modal open onClose={onClose} title="Replace with a library facet">
      <Input placeholder="Search facets…" value={q} onChange={e => setQ(e.target.value)} autoFocus />
      <div style={{ marginTop: 12, maxHeight: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {results === null ? <Loading /> : results.length === 0 ? <p style={{ fontSize: '0.85rem', color: 'var(--ink-faint)' }}>No matches.</p> :
          results.filter(f => f.variant).map(f => (
            <Card key={f.variant.id} hover onClick={() => onPick(f.variant.id)} style={{ padding: '10px 14px' }}>
              <b style={{ fontSize: '0.88rem' }}>{f.name}</b>
              <span style={{ color: 'var(--ink-faint)', fontSize: '0.76rem' }}> · {f.dimension} / {f.pillar} · {f.unitCount} units</span>
              {f.variant.definition && <div style={{ fontSize: '0.8rem', color: 'var(--ink-soft)', marginTop: 3 }}>{f.variant.definition.slice(0, 140)}…</div>}
            </Card>
          ))}
      </div>
    </Modal>
  );
}

export function Design() {
  const [trackId] = useRoleTrack();
  const [useCompass, setUseCompass] = useState(null); // null = loading
  useEffect(() => {
    if (!trackId) return;
    api.master.getRoleTrack(trackId).then(d => setUseCompass(d?.roleTrack?.useCompass !== false)).catch(() => setUseCompass(true));
  }, [trackId]);
  if (trackId && useCompass === false) return <DesignDirect trackId={trackId} />;
  return <DesignWithCompass trackId={trackId} />;
}

// Route B (model UC3) — no profile: choose the Units yourself from the catalogue,
// with lighter suggestions from the written need. Same programme, same Gate 2.
function DesignDirect({ trackId }) {
  return (
    <>
      <Note>
        <b>Designing without HB Compass.</b> Choose the Units you know are right, straight from the catalogue — the
        suggestions come from the written need, not a profile. An Ice Pilot who designs this way is using Basecamp correctly.
      </Note>
      <ProgrammeSection trackId={trackId} direct />
    </>
  );
}

function DesignWithCompass({ trackId }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [base, setBase] = useState('Inspiring Leadership');
  const [busy, setBusy] = useState(false);
  const [reword, setReword] = useState(null);   // { facet }
  const [replace, setReplace] = useState(null); // facetId
  const [tension, setTension] = useState({ poleA: '', poleB: '', note: '' });
  const [proposeOpen, setProposeOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    if (!trackId) { setState({ loading: false, data: null, error: null }); return; }
    setState(s => ({ ...s, loading: true }));
    api.master.getTrackProfile(trackId)
      .then(d => setState({ loading: false, data: d, error: null }))
      .catch(e => setState({ loading: false, data: null, error: e.message }));
  }, [trackId]);
  useEffect(() => { load(); }, [load]);

  if (!trackId) return <Alert type="info">Open a role track in <Link to="/master/eng" style={{ textDecoration: 'underline' }}>Needs</Link> first — Design always works on a role track.</Alert>;
  if (state.loading) return <Loading />;
  if (state.error) return <Alert type="error">{state.error}</Alert>;
  const d = state.data;

  async function propose() {
    setBusy(true);
    try { await api.master.proposeProfile(trackId, base); load(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  async function act(facetId, body) {
    try { await api.master.facetAction(d.profile.id, facetId, body); load(); }
    catch (e) { alert(e.message); }
  }
  async function addTension(e) {
    e.preventDefault();
    if (!tension.poleA.trim() || !tension.poleB.trim()) return;
    try { await api.master.addTension(d.profile.id, tension); setTension({ poleA: '', poleB: '', note: '' }); load(); }
    catch (err) { alert(err.message); }
  }
  async function exportPkg() {
    setExporting(true);
    try {
      const r = await api.master.exportProfile(d.profile.id);
      const blob = new Blob([JSON.stringify(r.package, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${d.profile.baseProfile}-profile-v${d.profile.version}.hbprofile.json`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      if (r.warnings && r.warnings.length) alert('Exported with warnings:\n\n' + r.warnings.join('\n'));
    } catch (e) { alert(e.message); } finally { setExporting(false); }
  }

  // No profile yet — assemble one from a base signature profile
  if (!d.profile) {
    return (
      <>
        <Note>The system assembles the whole role — four dimensions, ten pillars, forty facets — from the nearest signature profile plus the library. You then argue with it: accept, reword, replace or drop each facet.</Note>
        <SectionCard title="Assemble the Ideal Profile" style={{ maxWidth: 560 }}>
          <label style={fieldLabel}>Nearest signature profile (base)</label>
          <Select value={base} onChange={e => setBase(e.target.value)} style={{ maxWidth: 320 }}>
            {BASE_PROFILES.map(b => <option key={b} value={b}>{b}</option>)}
          </Select>
          <Btn loading={busy} onClick={propose} style={{ marginTop: 14 }}>Assemble profile from the library</Btn>
        </SectionCard>
      </>
    );
  }

  const { profile, reuse, facets, tensions } = d;
  const locked = ['locked', 'validated'].includes(profile.status);
  // group facets by dimension → pillar
  const byDim = {};
  facets.forEach(f => { (byDim[f.dimension] = byDim[f.dimension] || {}); (byDim[f.dimension][f.pillar] = byDim[f.dimension][f.pillar] || []).push(f); });

  return (
    <>
      {/* Header + reuse counter */}
      <Card style={{ padding: '18px 22px', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem' }}>{profile.baseProfile} → Ideal Profile <Badge status="default">v{profile.version}</Badge></div>
            <div style={{ fontSize: '0.8rem', color: 'var(--ink-faint)' }}>A hypothesis — present it as one. {profile.status}</div>
          </div>
          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', lineHeight: 1 }}>{reuse.fromLibrary} <span style={{ fontSize: '0.9rem', color: 'var(--ink-faint)' }}>of {reuse.total}</span></div>
            <div style={{ fontSize: '0.76rem', color: 'var(--ink-soft)' }}>from the library · {reuse.authored} newly authored</div>
          </div>
        </div>
        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--line)', flexWrap: 'wrap' }}>
          <Btn size="sm" variant="outline" loading={exporting} onClick={exportPkg}>Export HB Profile Package</Btn>
          {!locked && <Btn size="sm" variant="ghost" onClick={() => setProposeOpen(true)}>+ Propose new facet</Btn>}
        </div>
      </Card>

      {/* Facets grouped */}
      {Object.entries(byDim).map(([dim, pillars]) => (
        <SectionCard key={dim} title={dim}>
          {Object.entries(pillars).map(([pillar, fs]) => (
            <div key={pillar} style={{ marginBottom: 14 }}>
              <SectionLabel style={{ marginBottom: 2 }}>{pillar}</SectionLabel>
              {fs.map(f => (
                <div key={f.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 0', borderBottom: '1px solid var(--line)' }}>
                  <button onClick={() => act(f.id, { action: 'signature', value: !f.isSignature })} title="Signature facet"
                    style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '1rem', color: f.isSignature ? 'var(--gold, #C4872B)' : 'var(--ink-faint)' }}>
                    {f.isSignature ? '★' : '☆'}
                  </button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>{f.name}</span>
                    {f.mandatory && <span title="Holds the pillar" style={{ marginLeft: 6, fontSize: '0.72rem', color: 'var(--ink-faint)' }}>🔒</span>}
                    <span style={{ marginLeft: 8 }}><Badge status={ORIGIN_BADGE[f.origin] || 'default'}>{f.origin}</Badge></span>
                    <span style={{ marginLeft: 8, fontSize: '0.72rem', color: 'var(--ink-faint)' }}>{f.unitCount} units</span>
                    {f.definition && <div style={{ fontSize: '0.8rem', color: 'var(--ink-soft)', marginTop: 2 }}>{f.definition}</div>}
                  </div>
                  {!locked && (
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <Btn size="sm" variant="ghost" onClick={() => setReword({ facet: f })}>Reword</Btn>
                      <Btn size="sm" variant="ghost" onClick={() => setReplace(f.id)}>Replace</Btn>
                      {!f.mandatory && <Btn size="sm" variant="ghost" onClick={() => act(f.id, { action: 'drop' })} style={{ color: 'var(--danger)' }}>Drop</Btn>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </SectionCard>
      ))}

      {/* Tensions */}
      <SectionCard title="Role tensions" hint="The two-sided pulls built into the role (delivery today vs. upstream tomorrow). Naming them is what makes a customer say “you understood our job.”">
        {tensions.map(t => (
          <div key={t.id} style={{ fontSize: '0.88rem', padding: '5px 0', borderBottom: '1px solid var(--line)' }}>
            <b>{t.poleA}</b> ↔ <b>{t.poleB}</b>{t.note ? <span style={{ color: 'var(--ink-soft)' }}> — {t.note}</span> : null}
            {!locked && <button onClick={() => api.master.deleteTension(profile.id, t.id).then(load)} style={{ float: 'right', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--danger)' }}>✕</button>}
          </div>
        ))}
        {!locked && (
          <form onSubmit={addTension} style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <Input placeholder="Pole A" value={tension.poleA} onChange={e => setTension({ ...tension, poleA: e.target.value })} style={{ flex: 1, minWidth: 120 }} />
            <Input placeholder="Pole B" value={tension.poleB} onChange={e => setTension({ ...tension, poleB: e.target.value })} style={{ flex: 1, minWidth: 120 }} />
            <Input placeholder="Note (optional)" value={tension.note} onChange={e => setTension({ ...tension, note: e.target.value })} style={{ flex: 2, minWidth: 160 }} />
            <Btn size="sm" type="submit">Add</Btn>
          </form>
        )}
      </SectionCard>

      <ProgrammeSection trackId={trackId} />

      {reword && (
        <RewordModal facet={reword.facet} onClose={() => setReword(null)} onSave={async (body) => { await act(reword.facet.id, { action: 'reword', ...body }); setReword(null); }} />
      )}
      {replace && (
        <ReplacePicker onClose={() => setReplace(null)} onPick={async (variantId) => { await act(replace, { action: 'replace', variantId }); setReplace(null); }} />
      )}
      {proposeOpen && (
        <ProposeFacetModal onClose={() => setProposeOpen(false)} onSaved={() => { setProposeOpen(false); load(); }} />
      )}
    </>
  );
}

function ProposeFacetModal({ onClose, onSaved }) {
  const [f, setF] = useState({ dimension: 'Results', pillar: '', name: '', definition: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  async function save() {
    if (!f.pillar.trim() || !f.name.trim()) { alert('Pillar and name are required.'); return; }
    setBusy(true);
    try { await api.master.proposeFacet(f); alert('Proposed — it is in Contribute → My Approvals for an Adapted Profile Owner to decide. It becomes HB property once approved.'); onSaved(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  return (
    <Modal open onClose={onClose} title="Propose a new facet">
      <p style={{ fontSize: '0.8rem', color: 'var(--ink-soft)', marginBottom: 10 }}>Authored facets don’t enter the shared library until the Adapted Profile Owner approves them in Contribute — you can’t approve your own.</p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 140 }}>
          <label style={fieldLabel}>Dimension</label>
          <Select value={f.dimension} onChange={set('dimension')}>{['Results', 'Mindset', 'Skills', 'Influence'].map(o => <option key={o} value={o}>{o}</option>)}</Select>
        </div>
        <div style={{ flex: 2, minWidth: 160 }}>
          <label style={fieldLabel}>Pillar</label>
          <Input value={f.pillar} onChange={set('pillar')} placeholder="e.g. Long-Term Change" />
        </div>
      </div>
      <label style={{ ...fieldLabel, marginTop: 10 }}>Facet name</label>
      <Input value={f.name} onChange={set('name')} placeholder="e.g. Requirements Shaping & Co-Creation" />
      <label style={{ ...fieldLabel, marginTop: 10 }}>Definition</label>
      <textarea value={f.definition} onChange={set('definition')} rows={3} style={taStyle} />
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <Btn loading={busy} onClick={save}>Propose to Contribute</Btn>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
}

// ── Programme (M4) — day grid from the facet→Unit join, change types, gaps, Gate 2

function UnitPicker({ trackId, onComposed, onCancel }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState([]); // [{code, name}] in programme order
  const [days, setDays] = useState(2);
  const [busy, setBusy] = useState(false);
  const search = useCallback((term) => {
    api.master.unitSuggestions(trackId, term).then(setData).catch(e => setData({ error: e.message }));
  }, [trackId]);
  useEffect(() => { search(''); }, [search]);
  const toggle = (u) => setPicked(p => (p.some(x => x.code === u.code) ? p.filter(x => x.code !== u.code) : [...p, { code: u.code, name: u.name }]));
  const move = (i, dir) => setPicked(p => { const n = [...p]; const j = i + dir; if (j < 0 || j >= n.length) return p; [n[i], n[j]] = [n[j], n[i]]; return n; });
  async function compose() {
    setBusy(true);
    try { await api.master.composeProgrammeDirect(trackId, days, picked.map(p => p.code)); onComposed(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  const UnitRow = ({ u }) => {
    const on = picked.some(x => x.code === u.code);
    return (
      <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '7px 2px', borderBottom: '1px solid var(--line)', cursor: 'pointer' }}>
        <input type="checkbox" checked={on} onChange={() => toggle(u)} style={{ marginTop: 3 }} />
        <span style={{ minWidth: 0 }}>
          <b style={{ fontSize: '0.86rem' }}>{u.code}</b> <span style={{ fontSize: '0.86rem' }}>{u.name}</span>
          {u.usedBefore > 0 && <span style={{ marginLeft: 6, fontSize: '0.7rem', color: 'var(--ink-soft)', border: '1px solid var(--line)', borderRadius: 99, padding: '0 6px' }}>used for {u.usedBefore} customer{u.usedBefore > 1 ? 's' : ''} before</span>}
          {u.because && u.because.length > 0 && <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--ink-faint)' }}>matches the need: {u.because.join(', ')}</span>}
          {!u.because?.length && u.purpose && <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--ink-faint)' }}>{u.purpose.slice(0, 120)}{u.purpose.length > 120 ? '…' : ''}</span>}
        </span>
      </label>
    );
  };
  if (!data) return <Loading />;
  if (data.error) return <Alert type="error">{data.error}</Alert>;
  return (
    <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <div style={{ flex: '2 1 320px', minWidth: 0 }}>
        {!data.needWritten && <Alert type="warn">Write the need first (Needs Summary) — suggestions come from it. You can still pick from the catalogue.</Alert>}
        {data.suggestions.length > 0 && (
          <>
            <div style={{ ...fieldLabel, marginTop: 4 }}>Suggested from the written need</div>
            {data.suggestions.map(u => <UnitRow key={u.code} u={u} />)}
          </>
        )}
        <form onSubmit={e => { e.preventDefault(); search(q.trim()); }} style={{ display: 'flex', gap: 8, margin: '14px 0 6px' }}>
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search the catalogue — code, name, family…" />
          <Btn type="submit" size="sm" variant="outline">Search</Btn>
        </form>
        <div style={{ maxHeight: 320, overflowY: 'auto' }}>{data.catalogue.map(u => <UnitRow key={`c-${u.code}`} u={u} />)}</div>
      </div>
      <div style={{ flex: '1 1 240px', minWidth: 220, position: 'sticky', top: 20 }}>
        <div style={fieldLabel}>Your programme ({picked.length} Unit{picked.length === 1 ? '' : 's'})</div>
        {picked.length === 0 && <div style={{ fontSize: '0.82rem', color: 'var(--ink-faint)' }}>Tick the Units you want — in the order you’ll run them.</div>}
        {picked.map((p, i) => (
          <div key={p.code} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.84rem', padding: '4px 0' }}>
            <span style={{ color: 'var(--ink-faint)', width: 18 }}>{i + 1}.</span>
            <span style={{ flex: 1, minWidth: 0 }}><b>{p.code}</b> {p.name}</span>
            <button onClick={() => move(i, -1)} disabled={i === 0} style={pickBtn}>▲</button>
            <button onClick={() => move(i, 1)} disabled={i === picked.length - 1} style={pickBtn}>▼</button>
            <button onClick={() => toggle(p)} style={pickBtn} aria-label="Remove">×</button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginTop: 12, flexWrap: 'wrap' }}>
          <div>
            <label style={fieldLabel}>Days</label>
            <Input type="number" min="1" max="10" value={days} onChange={e => setDays(parseInt(e.target.value, 10) || 1)} style={{ width: 80 }} />
          </div>
          <Btn loading={busy} disabled={!picked.length} onClick={compose}>Lay out the days</Btn>
          {onCancel && <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>}
        </div>
      </div>
    </div>
  );
}
const pickBtn = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-faint)', padding: '0 3px', fontSize: '0.8rem' };

function ProgrammeSection({ trackId, direct = false }) {
  const [picking, setPicking] = useState(false);
  const [projectId] = useEngagement();
  const [days, setDays] = useState(5);
  const [busy, setBusy] = useState(false);
  const [ai, setAi] = useState(null); // M8 — { loading, title, draft, error }

  // M8 — deep content, per Unit on demand. Both return a DRAFT routed to Contribute.
  async function adaptUnit(code) {
    setAi({ loading: true, title: `Adapt Unit ${code} for this client` });
    try { const r = await api.ai.adaptUnit(projectId, code); setAi({ title: `Adapted Unit ${code}`, draft: r.draft }); }
    catch (e) { setAi({ title: `Adapt Unit ${code}`, error: e.message }); }
  }
  async function draftUnit(facet) {
    setAi({ loading: true, title: `Draft a new Unit for “${facet}”` });
    try { const r = await api.ai.draftUnit(projectId, [facet]); setAi({ title: `New Unit draft — ${facet}`, draft: r.draft }); }
    catch (e) { setAi({ title: `Draft new Unit`, error: e.message }); }
  }

  // No programme yet: start from the standard composition (W5), or the routes built before it —
  // compose from the profile (HB Compass) or choose the Units yourself (without HB Compass).
  const onEmpty = (data, reload) => (picking ? (
    <SectionCard title="Programme" hint={<>The day grid from the Units you chose. Adapt a Unit for this client where it needs it — the adaptation goes to the group for approval.</>}>
      <UnitPicker trackId={trackId} onComposed={() => { setPicking(false); reload(); }} onCancel={() => setPicking(false)} />
    </SectionCard>
  ) : (
    <DesignStart trackId={trackId} data={data} onStarted={reload}>
      {direct ? (
        <Btn variant="outline" onClick={() => setPicking(true)}>Or choose the Units yourself</Btn>
      ) : (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={fieldLabel}>Days</label>
            <Input type="number" min="1" max="10" value={days} onChange={e => setDays(parseInt(e.target.value, 10) || 5)} style={{ width: 90 }} />
          </div>
          <Btn variant="outline" loading={busy} onClick={async () => {
            setBusy(true);
            try { await api.master.composeProgramme(trackId, days); reload(); }
            catch (e) { alert(e.message); } finally { setBusy(false); }
          }}>Compose the day grid</Btn>
        </div>
      )}
    </DesignStart>
  ));

  return (
    <>
      <DesignEditor
        trackId={trackId}
        onEmpty={onEmpty}
        onGateChanged={notifyWorkspace}
        gate2Wording={direct
          ? 'The client approves the programme you composed from the Units — confirming it is Gate 2.'
          : 'They are one decision seen twice — approving the profile without looking at what develops it is how programmes end up generic.'}
        after={<ContinueLink to="/master/proposals" step="proposal">Continue to Proposal →</ContinueLink>}
        slotTools={(s) => (s.kind !== 'new' && s.units.length > 0
          ? <Btn size="sm" variant="ghost" onClick={() => adaptUnit(s.units[0])} title="Adapt this Unit for the client (AI draft → Contribute)">Adapt</Btn>
          : s.kind === 'new'
            ? <Btn size="sm" variant="ghost" onClick={() => draftUnit(s.content)} title="Draft a new Unit for this gap (AI draft → Contribute)">Draft Unit</Btn>
            : null)}
      />

      {ai && (
        <Modal open onClose={() => setAi(null)} title={ai.title || 'Deep content'}>
          {ai.loading ? (
            <div style={{ textAlign: 'center', padding: '30px 0' }}><Spinner /><p style={{ marginTop: 12, fontSize: '0.85rem', color: 'var(--ink-soft)' }}>Claude is drafting…</p></div>
          ) : ai.error ? <Alert type="error">{ai.error}</Alert> : (
            <div>
              <Alert type="info">Draft only — it is attached to <Link to="/contribute/myapprovals" style={{ textDecoration: 'underline' }}>Contribute → My Approvals</Link>. Approved, it becomes HB property credited to you.</Alert>
              <pre style={{ marginTop: 12, padding: '12px 14px', background: 'var(--canvas-warm)', borderRadius: 'var(--radius-md)', fontSize: '0.8rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', fontFamily: 'var(--font-body)', maxHeight: 360, overflowY: 'auto' }}>{ai.draft}</pre>
            </div>
          )}
        </Modal>
      )}
    </>
  );
}

// ── Ask (M6) — send the needs questionnaire to project parties ───────────────
function AskPanel({ trackId, parties, locked }) {
  const list = parties || [];
  const [forms, setForms] = useState(null);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.master.getForms(trackId).then(d => setForms(d.forms)).catch(() => setForms([])); }, [trackId]);
  useEffect(() => { load(); }, [load]);
  const toggle = (id) => setSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  async function send() {
    if (!sel.length) return;
    setBusy(true);
    try { await api.master.sendForm(trackId, 'needs_intake', sel); setSel([]); load(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  return (
    <SectionCard title="Ask — send the needs questionnaire" hint="Send a short questionnaire to the parties on the project. Their answers land back here as intake, ready to synthesise.">
      {list.length === 0 ? (
        <Alert type="warn">No parties on the project yet — add them on the project card in <Link to="/explore/crm" style={{ textDecoration: 'underline' }}>Explore</Link>.</Alert>
      ) : (
        <>
          {!locked && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
              {list.map(p => (
                <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.86rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={sel.includes(p.id)} onChange={() => toggle(p.id)} />
                  <span>{p.name}<span style={{ color: 'var(--ink-faint)', fontSize: '0.78rem' }}> · {p.role}{p.contact ? ` · ${p.contact}` : ''}</span></span>
                </label>
              ))}
              <Btn size="sm" loading={busy} disabled={!sel.length} onClick={send} style={{ alignSelf: 'flex-start', marginTop: 4 }}>Send questionnaire</Btn>
            </div>
          )}
          {forms && forms.length > 0 && (
            <div style={{ borderTop: '1px solid var(--line)', paddingTop: 10 }}>
              {forms.map(f => (
                <div key={f.id} style={{ fontSize: '0.84rem', marginBottom: 8 }}>
                  <b>{f.template === 'role_definition' ? 'Role validation' : 'Needs questionnaire'}</b>
                  <span style={{ color: f.responded === f.total ? 'var(--teal)' : 'var(--ink-soft)', marginLeft: 8 }}>{f.responded}/{f.total} responded</span>
                  <details style={{ marginTop: 4 }}>
                    <summary style={{ fontSize: '0.78rem', color: 'var(--ink-faint)', cursor: 'pointer' }}>recipient links</summary>
                    {f.recipients.map((r, i) => (
                      <div key={i} style={{ fontSize: '0.8rem', padding: '3px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>{r.responded ? '✓' : '•'} {r.name}</span>
                        <button onClick={() => { navigator.clipboard?.writeText(r.link); }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--ink)', textDecoration: 'underline', fontSize: '0.78rem', fontFamily: 'var(--font-body)' }}>copy link</button>
                      </div>
                    ))}
                  </details>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </SectionCard>
  );
}

function RewordModal({ facet, onClose, onSave }) {
  const [displayName, setDisplayName] = useState(facet.name);
  const [definition, setDefinition] = useState(facet.definition || '');
  const [busy, setBusy] = useState(false);
  return (
    <Modal open onClose={onClose} title="Reword this facet">
      <p style={{ fontSize: '0.8rem', color: 'var(--ink-soft)', marginBottom: 10 }}>Rewording keeps what the facet measures (its canonical identity and Unit matches) — only the wording changes.</p>
      <label style={fieldLabel}>Display name</label>
      <Input value={displayName} onChange={e => setDisplayName(e.target.value)} />
      <label style={{ ...fieldLabel, marginTop: 10 }}>Definition</label>
      <textarea value={definition} onChange={e => setDefinition(e.target.value)} rows={4} style={taStyle} />
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <Btn loading={busy} onClick={async () => { setBusy(true); try { await onSave({ displayName, definition }); } finally { setBusy(false); } }}>Save wording</Btn>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
}

// ── Proposal (M5) — artefact checklist, commercials (CRM rollup), Gate 3 ─────
// HB Way of Selling v1.1: sending is a milestone; the decision maker accepting it in principle is Gate 3
// (→ Closing); the signed agreement is Gate 4, confirmed in the customer workspace.
const ART_STATUS = { pending: 'default', attached: 'pending', sent: 'active' };

export function ProposalScreen() {
  const [trackId] = useRoleTrack();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [currency, setCurrency] = useState('EUR');
  const [attachFor, setAttachFor] = useState(null);
  const [attachUrl, setAttachUrl] = useState('');

  const load = useCallback(() => {
    if (!trackId) { setState({ loading: false, data: null, error: null }); return; }
    api.master.getTrackProposal(trackId)
      .then((pr) => {
        setState({ loading: false, data: pr, error: null });
        if (pr.proposal) { setItems(pr.commercials.lineItems || []); setCurrency(pr.commercials.currency || 'EUR'); }
      })
      .catch(e => setState({ loading: false, data: null, error: e.message }));
  }, [trackId]);
  useEffect(() => { load(); }, [load]);

  if (!trackId) return <Alert type="info">Open a role track in <Link to="/master/eng" style={{ textDecoration: 'underline' }}>Needs</Link> first — the proposal belongs to a role track.</Alert>;
  if (state.loading) return <Loading />;
  if (state.error) return <Alert type="error">{state.error}</Alert>;
  const d = state.data;

  async function initP() { setBusy(true); try { await api.master.initProposal(trackId); load(); notifyWorkspace(); } catch (e) { alert(e.message); } finally { setBusy(false); } }

  if (!d.proposal) {
    return (
      <>
        <Note>The proposal step — a checklist of artefacts (mandatory pre-ticked) and the commercials. Every artefact opens for review before it is marked sent; the CRM deal value follows the commercials here, so nobody types a number twice.</Note>
        <SectionCard title="Open the proposal" tone="gate" hint="Seeds the artefact checklist from this engagement (the mapping memo is mandatory when you captured client vocabulary)." style={{ maxWidth: 560 }}>
          <Btn loading={busy} onClick={initP}>Open the proposal</Btn>
        </SectionCard>
      </>
    );
  }

  const pStatus = d.proposal.status || 'draft';
  const locked = pStatus === 'sent' || pStatus === 'accepted';
  async function art(id, body) { try { await api.master.artefactAction(id, body); load(); } catch (e) { alert(e.message); } }
  async function saveCommercials() { setBusy(true); try { await api.master.updateCommercials(d.proposal.id, { lineItems: items, currency }); load(); notifyWorkspace(); } catch (e) { alert(e.message); } finally { setBusy(false); } }
  async function setStatus(status) { setBusy(true); try { await api.master.updateProposal(d.proposal.id, status); load(); notifyWorkspace(); } catch (e) { alert(e.message); } finally { setBusy(false); } }
  async function doAttach(id) { if (!attachUrl.trim()) return; await art(id, { action: 'attach', url: attachUrl.trim() }); setAttachFor(null); setAttachUrl(''); }
  const setItem = (i, k, v) => setItems(items.map((it, j) => j === i ? { ...it, [k]: v } : it));
  const total = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.rate) || 0), 0);
  const mand = d.artefacts.filter(a => a.mandatory);
  const opt = d.artefacts.filter(a => !a.mandatory);

  const artefactRow = (a) => (
    <div key={a.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--line)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {a.mandatory
          ? <span title="Mandatory" style={{ color: 'var(--ink-faint)' }}>🔒</span>
          : <input type="checkbox" checked={a.included} disabled={locked} onChange={e => art(a.id, { action: 'include', included: e.target.checked })} />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: '0.88rem', fontWeight: 600, opacity: a.included ? 1 : 0.5 }}>{a.type}</span>
          <span style={{ fontSize: '0.72rem', color: 'var(--ink-faint)', marginLeft: 6 }}>{a.audience} · {a.generatedBy}</span>
          {d.useCompass === false && a.type === 'Ideal Profile document' && (
            <div style={{ fontSize: '0.74rem', color: 'var(--ink-soft)', marginTop: 2 }}>Optional — this role track is designed without HB Compass. Attach a profile document if you have one.</div>
          )}
        </div>
        <Badge status={ART_STATUS[a.status]}>{a.status}</Badge>
        {a.included && !locked && (
          <span style={{ display: 'inline-flex', gap: 4 }}>
            <Btn size="sm" variant="ghost" onClick={() => { setAttachFor(a.id); setAttachUrl(a.url || ''); }}>{a.url ? 'Re-attach' : 'Attach file'}</Btn>
            {a.status === 'attached' && <Btn size="sm" variant="outline" onClick={() => art(a.id, { action: 'sent' })}>Mark sent</Btn>}
          </span>
        )}
        {a.url && <a href={a.url} target="_blank" rel="noreferrer" style={{ fontSize: '0.76rem' }}>open ↗</a>}
      </div>
      {attachFor === a.id && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <Input placeholder="Paste a file link (SharePoint / URL)…" value={attachUrl} onChange={e => setAttachUrl(e.target.value)} style={{ flex: 1 }} autoFocus />
          <Btn size="sm" onClick={() => doAttach(a.id)}>Save</Btn>
          <Btn size="sm" variant="ghost" onClick={() => { setAttachFor(null); setAttachUrl(''); }}>Cancel</Btn>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div style={contextBarStyle}>
        <span>Proposal <b>v{d.proposal.version}</b> · {d.proposal.status}. Every generated artefact opens for review before it can be marked sent — the system is good, but you are accountable.</span>
      </div>

      {/* Artefacts */}
      <SectionCard title="Artefacts" hint="Mandatory items are locked in; tick the optional ones you need. Attach hand-made files where generation isn’t built yet — they land in the project’s Documents.">
        {mand.map(artefactRow)}
        {opt.length > 0 && <SectionLabel style={{ margin: '16px 0 2px' }}>Optional</SectionLabel>}
        {opt.map(artefactRow)}
      </SectionCard>

      {/* Commercials */}
      <SectionCard title="Commercials" hint="The CRM deal value is a rollup of this — editing it changes the funnel’s pipeline figure.">
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: 480, borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead><tr style={{ textAlign: 'left', color: 'var(--muted)', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '2px solid var(--ink-mid)' }}>
              <th style={{ padding: '4px 6px' }}>Item</th><th style={{ padding: '4px 6px', width: 70 }}>Qty</th><th style={{ padding: '4px 6px', width: 90 }}>Unit</th><th style={{ padding: '4px 6px', width: 90 }}>Rate</th><th style={{ padding: '4px 6px', width: 100, textAlign: 'right' }}>Amount</th><th></th>
            </tr></thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i}>
                  <td style={{ padding: '3px 6px' }}><Input value={it.label || ''} onChange={e => setItem(i, 'label', e.target.value)} disabled={locked} /></td>
                  <td style={{ padding: '3px 6px' }}><Input type="number" value={it.qty ?? ''} onChange={e => setItem(i, 'qty', e.target.value)} disabled={locked} /></td>
                  <td style={{ padding: '3px 6px' }}><Input value={it.unit || ''} onChange={e => setItem(i, 'unit', e.target.value)} disabled={locked} placeholder="days" /></td>
                  <td style={{ padding: '3px 6px' }}><Input type="number" value={it.rate ?? ''} onChange={e => setItem(i, 'rate', e.target.value)} disabled={locked} /></td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{((Number(it.qty) || 0) * (Number(it.rate) || 0)).toLocaleString()}</td>
                  <td>{!locked && <button onClick={() => setItems(items.filter((_, j) => j !== i))} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--danger)' }}>✕</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
          {!locked && <Btn size="sm" variant="ghost" onClick={() => setItems([...items, { label: '', qty: 1, unit: 'days', rate: 0 }])}>+ Add line</Btn>}
          <Input value={currency} onChange={e => setCurrency(e.target.value.toUpperCase().slice(0, 3))} disabled={locked} style={{ width: 70 }} />
          <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-display)', fontSize: '1.2rem' }}>{total.toLocaleString()} {currency}</span>
          {!locked && <Btn size="sm" loading={busy} onClick={saveCommercials}>Save &amp; roll up to CRM</Btn>}
        </div>
      </SectionCard>

      {/* Sent (milestone) → accepted in principle (Gate 3) */}
      <SectionCard title="Gate 3 — proposal accepted" tone={pStatus === 'accepted' ? 'done' : 'gate'}>
        {pStatus === 'draft' && (
          <>
            <p style={{ fontSize: '0.86rem', color: 'var(--ink-soft)', marginBottom: 10 }}>Send the proposal and present it to the decision maker. Sending is a milestone — the project stays in Proposal until the decision maker accepts it in principle.</p>
            <Btn loading={busy} onClick={() => setStatus('sent')}>Mark the proposal sent</Btn>
          </>
        )}
        {pStatus === 'sent' && (
          <>
            <p style={{ fontSize: '0.86rem', color: 'var(--ink-soft)', marginBottom: 12 }}>✓ Sent. When the decision maker accepts the proposal in principle, confirm Gate 3 — the project moves to Closing.</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Btn loading={busy} onClick={() => setStatus('accepted')}>Accepted in principle — Gate 3</Btn>
              <Btn variant="outline" loading={busy} onClick={() => setStatus('declined')}>Declined</Btn>
              <Btn variant="ghost" loading={busy} onClick={() => setStatus('draft')} style={{ marginLeft: 'auto' }}>Back to draft</Btn>
            </div>
          </>
        )}
        {pStatus === 'accepted' && (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <p style={{ fontSize: '0.86rem', color: 'var(--success)', margin: 0, flex: 1, minWidth: 240 }}>✓ Accepted in principle. The project moved to <b>Closing</b> — when the agreement is signed, confirm Gate 4 in the customer workspace.</p>
            <Btn size="sm" variant="ghost" loading={busy} onClick={() => setStatus('sent')}>Reopen</Btn>
          </div>
        )}
        {pStatus === 'declined' && (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <p style={{ fontSize: '0.86rem', color: 'var(--ink-soft)', margin: 0, flex: 1, minWidth: 240 }}>Declined. Rework it and send again, or record the outcome (lost, with the reason) in the customer workspace.</p>
            <Btn size="sm" variant="ghost" loading={busy} onClick={() => setStatus('draft')}>Back to draft</Btn>
          </div>
        )}
      </SectionCard>
    </>
  );
}

const TITLES = {
  overview: 'sell & adapt',
  method: 'Methodology', profiles: 'Signature Profiles (learn)', eng: 'Needs',
  builder: 'Design', proposals: 'Proposal', ypl: 'Reference case — YPL',
};

// W4: the work moved into the customer workspace. Old /master/{eng,builder,proposals}
// bookmarks resolve to the last-used customer's workspace (or the Journeys list).
function workRedirectTarget() {
  const pid = localStorage.getItem(ENGAGEMENT_KEY);
  return pid ? `/customers/${pid}` : '/customers/journeys';
}

// "Where did the work go?" — shown once on the Master shelf during the transition.
function ShelfMovedNote() {
  return (
    <Alert type="info">
      <b>Needs, Design and Proposal moved into the customer workspace</b> — open{' '}
      <Link to="/customers/journeys" style={{ textDecoration: 'underline' }}>My Pipeline → a customer</Link>;
      the steps sit at their stages, behind the gates. This section keeps what you learn and reuse.
    </Alert>
  );
}

export default function Master() {
  const { sub } = useParams();
  if (!sub) return <Navigate to="/master/overview" replace />;

  // Project-bound screens no longer live on the shelf → redirect to the workspace.
  if (sub === 'eng' || sub === 'builder' || sub === 'proposals') {
    return <Navigate to={workRedirectTarget()} replace />;
  }

  let body;
  if (sub === 'overview') body = <><ShelfMovedNote /><PhaseOverview phase="M" /></>;
  else if (sub === 'method') body = <Method />;
  else if (sub === 'profiles') body = <ProfilesLearn />;
  else if (sub === 'ypl') body = <Ypl />;
  else return <Navigate to="/master/overview" replace />;

  return (
    <BasecampLayout>
      <PageHeader title="Master — method, profiles & the facet library" subtitle={TITLES[sub]} />
      <AreaLayout items={SUBNAV}>{body}</AreaLayout>
    </BasecampLayout>
  );
}
